import { Document, Security } from '@carbon/icons-react'
import {
  AspectRatio,
  Box,
  Flex,
  Image,
  Link,
  useToken,
  Text,
  Icon,
  Badge,
} from '@chakra-ui/react'
import { memo } from 'react'
import Truncate from 'react-truncate'

import { useAppRoute } from 'routes/utils'

import { defaultWebMeta } from 'api/types'
import useCollectionMeta from 'api/useCollectionMeta'

import { IBaseView } from 'interfaces/navigationPage.interface'

import { formatDate } from 'utils/formatDate'
import CountryFlags from 'config/common/tagColours/CountryFlags'
import MediaTagsColors from 'config/common/tagColours/MediaTagsColors'

export const Article = memo(
  ({
    id,
    url,
    date,
    title,
    isKeyNews,
    modelEndpoint,
    airtableBase,
    trackMediaClick,
    countries,
    tags,
    tagsColors,
  }: {
    id: string | number
    url: string
    date: string
    title: string
    isKeyNews: boolean
    modelEndpoint: string
    airtableBase: IBaseView<any>['airtableBase']
    trackMediaClick: (e: any) => void
    tags?: string[]
    tagsColors?: Record<string, string>
    countries?: string[]
  }) => {
    const app = useAppRoute()
    const { data } = useCollectionMeta(app, modelEndpoint, { airtableBase }, [
      id,
    ])

    const { image, logo, description, publisher, lang } = data
      ? data[0]
      : defaultWebMeta

    const red = useToken('colors', 'red.600')

    const _tags = [...new Set(tags)]
    const _countries = [...new Set(countries)]

    return (
      <Box
        onClick={() => trackMediaClick(data)}
        {...(isKeyNews ? { borderLeft: `10px solid ${red}` } : {})}
      >
        <Link
          display='flex'
          px={4}
          href={url}
          target='_blank'
          py={3}
          width='100%'
          fontWeight={500}
          fontSize='sm'
          lineHeight='1.4'
          borderBottom='1px solid'
          borderColor='gray.100'
          _hover={{
            bg: 'gray.300',
            textDecoration: 'none',
          }}
        >
          <Box minWidth='75px' mr={3}>
            <AspectRatio
              ratio={1}
              rounded='md'
              bg='white'
              overflow='hidden'
              border='1px solid'
              borderColor='gray.100'
            >
              {image && image.url ? (
                <Image src={image.url} objectFit='cover' />
              ) : (
                <Box bg='gray.100'>
                  <Icon as={Document} boxSize='30px' />
                </Box>
              )}
            </AspectRatio>
          </Box>
          <Box width='85%'>
            <Flex gap='2' alignItems={'center'}>
              <Box
                textTransform='uppercase'
                fontSize='xs'
                color='gray.600'
                lineHeight='none'
              >
                {formatDate(date)}
              </Box>
              {_countries?.map((country) => (
                <Flex alignItems={'center'}>
                  <Text fontSize={'xs'} whiteSpace={'nowrap'}>
                    {CountryFlags[country]}
                  </Text>
                </Flex>
              ))}
              {_tags?.map((tag) => (
                <Box
                  bg={tagsColors?.[tag] || MediaTagsColors[tag] || 'gray4'}
                  w='fit-content'
                  px='6px'
                  borderRadius={'lg'}
                  my='8px'
                >
                  <Text fontSize={'2xs'} whiteSpace={'nowrap'}>
                    {tag}
                  </Text>
                </Box>
              ))}
            </Flex>
            <Box fontWeight={500} mb={1} lineHeight='none'>
              <Text lineHeight={'16px'}>{title}</Text>
            </Box>
            <Box fontSize='xs' opacity={0.5} mb={2}>
              <Truncate lines={1}>{description}</Truncate>
            </Box>
            <Box display='flex' alignItems='center'>
              {logo && logo.url && (
                <Box
                  width='16px'
                  border='1px solid'
                  rounded='sm'
                  borderColor='gray.200'
                  overflow='hidden'
                  bg='white'
                  mr={1}
                >
                  <AspectRatio ratio={1}>
                    <Image src={logo.url} objectFit='contain' />
                  </AspectRatio>
                </Box>
              )}
              <Box fontSize='xs' lineHeight='none' color='gray.600'>
                {publisher}
              </Box>
            </Box>
            <Badge
              fontSize='xx-small'
              float='right'
              colorScheme='blackAlpha'
              px={1}
            >
              {lang}
            </Badge>
          </Box>
          {isKeyNews && (
            <Flex
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
            >
              <Security size={24} fontSize={30} color={red} />
              <Text textAlign='center' fontSize='sm' color={red}>
                Key Intel
              </Text>
            </Flex>
          )}
        </Link>
      </Box>
    )
  }
)
